import { useMemo, useRef } from 'react'

import type {
  CounterSigner,
  QuoteContact
} from 'modules/Cube/domain/cube.domain.types'

export type RecipientRowKind = 'SIGNER' | 'CC' | 'COUNTER_SIGNER'

/** A countersigner is fixed by the quote's settings, so only these two swap. */
export type EditableRecipientType = Exclude<RecipientRowKind, 'COUNTER_SIGNER'>

export type QuoteRecipientRow = {
  /** The contact id, or the countersigner id for a countersigner row. */
  id: string
  name: string
  email: string
  kind: RecipientRowKind
  lastNotifiedAt: string | undefined
  signedAt: string | undefined
}

/**
 * Everything the rows are built from. Both the API's quote and Cube's domain
 * quote have this shape, so a caller can pass whichever it holds - the
 * published variant when it wants notification and signing state, the domain
 * quote for a draft that has never been published.
 */
export type QuoteRecipientsSource = {
  contacts: QuoteContact[]
  ccContacts?: QuoteContact[]
  counterSigners: CounterSigner[]
}

export type QuoteRecipientRowsOptions = {
  /**
   * The account setting: countersigners are only asked once every signer has
   * signed, so until that happens they aren't a recipient yet - including on a
   * quote with no signers on it at all.
   */
  notifyCounterSignerLast?: boolean
  /**
   * Drop anyone who has already signed - there is nothing left to ask them.
   * The publish modal keeps them; the notify modal in PR7 is the caller that
   * turns this on.
   */
  excludeSigned?: boolean
}

export const useQuoteRecipientRows = (
  quote: QuoteRecipientsSource | undefined,
  options: QuoteRecipientRowsOptions = {}
): { rows: QuoteRecipientRow[]; showCounterSignerLastNote: boolean } => {
  const { notifyCounterSignerLast = false, excludeSigned = false } = options
  /**
   * The order the list was first built in. Rows are sorted back into it every
   * time, so moving somebody from signer to cc leaves them where they are
   * rather than jumping them down to the cc group - under the pointer that
   * just changed them. Anyone new joins the end.
   */
  const orderFromMount = useRef<string[]>([])

  return useMemo(() => {
    const contactRows = (quote?.contacts ?? []).map(contact =>
      toRow(contact.contactId, contact, 'SIGNER')
    )
    const ccRows = (quote?.ccContacts ?? []).map(contact =>
      toRow(contact.contactId, contact, 'CC')
    )
    const counterSignerRows = (quote?.counterSigners ?? []).map(counterSigner =>
      toRow(counterSigner.id, counterSigner, 'COUNTER_SIGNER')
    )

    // Someone listed twice keeps the first row they appear in, so a contact who
    // is also a cc recipient shows as a signer - the same rule the quote itself
    // applies when it saves the two lists.
    const rows = inFirstSeenOrder(
      dedupeById([...contactRows, ...ccRows, ...counterSignerRows]),
      orderFromMount
    )
    const visibleRows = excludeSigned ? rows.filter(row => !row.signedAt) : rows

    // A countersigner signs the quote back once the other side has signed it,
    // so they are only a recipient once that has happened. A quote with no
    // signers on it never gets there - only cc contacts added so far, say - so
    // that holds them back too.
    const signerRows = rows.filter(row => row.kind === 'SIGNER')
    const everySignerHasSigned =
      signerRows.length > 0 && signerRows.every(row => row.signedAt)
    const hideCounterSigners =
      notifyCounterSignerLast &&
      !everySignerHasSigned &&
      visibleRows.some(row => row.kind === 'COUNTER_SIGNER')

    return {
      rows: hideCounterSigners
        ? visibleRows.filter(row => row.kind !== 'COUNTER_SIGNER')
        : visibleRows,
      showCounterSignerLastNote: hideCounterSigners
    }
  }, [quote, notifyCounterSignerLast, excludeSigned])
}

const SIGNING_KINDS: RecipientRowKind[] = ['SIGNER', 'COUNTER_SIGNER']

/** Writing to a signer or countersigner is what asks them to sign. */
export const isSigningRow = (row: QuoteRecipientRow): boolean =>
  SIGNING_KINDS.includes(row.kind)

/**
 * The emails to send to, for the rows the user ticked. This is the only place
 * row ids become the emails the API takes, and the only place the two can
 * disagree - one person can hold a contact row and a countersigner row, and
 * they should still only be written to once.
 */
export const recipientEmailsFor = (
  rows: QuoteRecipientRow[],
  selectedIds: string[]
): string[] => {
  const selected = new Set(selectedIds)

  return Array.from(
    new Set(rows.filter(row => selected.has(row.id)).map(row => row.email))
  )
}

const toRow = (
  id: string,
  contact: {
    name: string | undefined
    email: string
    /** Optional, since a cc contact never carries one. */
    signedAt?: string
    notificationHistory?: { notifiedAt: string }[]
  },
  kind: RecipientRowKind
): QuoteRecipientRow => ({
  id,
  name: contact.name || contact.email,
  email: contact.email,
  kind,
  // The history is newest first, so the most recent email is the one at the top.
  lastNotifiedAt: contact.notificationHistory?.at(0)?.notifiedAt,
  signedAt: contact.signedAt
})

/**
 * Sorts rows into the order their ids were first seen in, recording any new
 * ones at the end as it goes. Two rows swapping lists swap nothing here.
 */
const inFirstSeenOrder = (
  rows: QuoteRecipientRow[],
  order: { current: string[] }
): QuoteRecipientRow[] => {
  const unseen = rows
    .map(row => row.id)
    .filter(id => !order.current.includes(id))

  order.current = [...order.current, ...unseen]

  return [...rows].sort(
    (a, b) => order.current.indexOf(a.id) - order.current.indexOf(b.id)
  )
}

const dedupeById = (rows: QuoteRecipientRow[]): QuoteRecipientRow[] =>
  rows.reduce<QuoteRecipientRow[]>(
    (acc, row) =>
      acc.some(existing => existing.id === row.id) ? acc : [...acc, row],
    []
  )
